import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import Navbar from './components/Navbar';
import ExpeditionPage from './pages/ExpeditionPage';
import DatabasePage from './pages/DatabasePage';
import AnalyticsPage from './pages/AnalyticsPage';
import ProfilePage from './pages/ProfilePage';
import DocumentationPage from './pages/DocumentationPage';
import DriverAppPage from './pages/DriverAppPage';
import DriverConnectPage from './pages/DriverConnectPage';
import ConnectPage from './pages/ConnectPage';
import LoginPage from './pages/LoginPage';
import AdminTenantsPage from './pages/AdminTenantsPage';

export default function App() {
  const isNative = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
  const isDriverRoute = window.location.pathname.startsWith('/driver');
  const isExplicitConnect = window.location.pathname.startsWith('/driver-connect');

  const urlDriverId = typeof window !== 'undefined' ? (window.location.pathname.match(/^\/driver\/(\d+)/)?.[1] || '') : '';
  const urlHasToken = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).has('token') : false;

  const [savedDriverId, setSavedDriverId] = useState(() => urlDriverId || localStorage.getItem('loops_driver_id') || '');
  const [savedServerUrl, setSavedServerUrl] = useState(() => localStorage.getItem('loops_driver_server_url') || '');
  const [showDriverConnect, setShowDriverConnect] = useState(() => isExplicitConnect || urlHasToken || (!urlDriverId && !localStorage.getItem('loops_driver_id')));

  // Auth State
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('loops_auth_token') || '');
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const u = localStorage.getItem('loops_user_data');
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  });

  const [impersonatedTenantId, setImpersonatedTenantId] = useState(() => {
    const id = localStorage.getItem('loops_impersonate_tenant_id');
    return id ? parseInt(id) : null;
  });
  const [impersonatedTenantName, setImpersonatedTenantName] = useState(() => localStorage.getItem('loops_impersonate_tenant_name') || '');

  // Global fetch interceptor to automatically attach Authorization header and x-tenant-override
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (url, options = {}) => {
      const token = localStorage.getItem('loops_auth_token');
      const impersonatedId = localStorage.getItem('loops_impersonate_tenant_id');

      if (typeof url === 'string' && url.startsWith('/api')) {
        const headers = new Headers(options.headers || {});
        if (token && !headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        if (impersonatedId && !headers.has('x-tenant-override')) {
          headers.set('x-tenant-override', String(impersonatedId));
        }
        options = { ...options, headers };
      }
      return originalFetch(url, options);
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // Verify auth on mount
  useEffect(() => {
    if (authToken) {
      fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${authToken}` }
      })
        .then(r => {
          if (!r.ok) throw new Error('Token invalid or expired');
          return r.json();
        })
        .then(data => {
          if (data.user) {
            setCurrentUser(data.user);
            localStorage.setItem('loops_user_data', JSON.stringify(data.user));
          } else {
            handleLogout();
          }
        })
        .catch(() => handleLogout());
    } else {
      handleLogout();
    }
  }, [authToken]);

  const handleLogout = () => {
    localStorage.removeItem('loops_auth_token');
    localStorage.removeItem('loops_user_data');
    localStorage.removeItem('loops_impersonate_tenant_id');
    localStorage.removeItem('loops_impersonate_tenant_name');
    setAuthToken('');
    setCurrentUser(null);
    setImpersonatedTenantId(null);
    setImpersonatedTenantName('');
  };

  const handleSwitchTenant = (tenantId, tenantName) => {
    if (tenantId === 1 && currentUser?.tenantId === 1) {
      localStorage.removeItem('loops_impersonate_tenant_id');
      localStorage.removeItem('loops_impersonate_tenant_name');
      setImpersonatedTenantId(null);
      setImpersonatedTenantName('');
    } else {
      localStorage.setItem('loops_impersonate_tenant_id', String(tenantId));
      localStorage.setItem('loops_impersonate_tenant_name', tenantName);
      setImpersonatedTenantId(tenantId);
      setImpersonatedTenantName(tenantName);
    }
    loadData(null, 0, true);
  };

  const handleClearImpersonate = () => {
    localStorage.removeItem('loops_impersonate_tenant_id');
    localStorage.removeItem('loops_impersonate_tenant_name');
    setImpersonatedTenantId(null);
    setImpersonatedTenantName('');
    loadData(null, 0, true);
  };

  const [activeTab, setActiveTab] = useState('expedition');
  const [wsConnected, setWsConnected] = useState(false);

  // Initial Startup Video Splash State
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [splashFading, setSplashFading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);

  const dismissSplash = () => {
    setSplashFading(true);
    setTimeout(() => {
      setIsInitialLoading(false);
    }, 600);
  };

  const handleVideoEnded = () => {
    setVideoEnded(true);
    if (isDataLoaded) {
      dismissSplash();
    }
  };

  // State
  const [isPg, setIsPg] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [driverShifts, setDriverShifts] = useState([]);
  const [objects, setObjects] = useState([]);
  const [stopAttachments, setStopAttachments] = useState([]);
  const [returnTickets, setReturnTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientDefaultTimeWindows, setClientDefaultTimeWindows] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stops, setStops] = useState([]);
  const [unassignedStops, setUnassignedStops] = useState([]);
  const [stopDependencies, setStopDependencies] = useState([]);
  const [jobPauses, setJobPauses] = useState([]);
  const [distanceMatrix, setDistanceMatrix] = useState([]);
  const [distanceMatrixCount, setDistanceMatrixCount] = useState(0);
  const [matrixProgress, setMatrixProgress] = useState(null);
  const [realtimeEvents, setRealtimeEvents] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [currentExpeditionFilter, setCurrentExpeditionFilter] = useState(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    return { startDate: todayStr, endDate: todayStr, isAll: false };
  });

  const formatLocalDateISO = (date) => {
    const d = date instanceof Date ? date : new Date(date || Date.now());
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const loadData = async (targetDateOrRange = null, windowDays = 0, forceAutoFallback = false) => {
    try {
      const activeFilter = (targetDateOrRange && typeof targetDateOrRange === 'object' && !(targetDateOrRange instanceof Date))
        ? targetDateOrRange
        : (typeof targetDateOrRange === 'string'
            ? { startDate: targetDateOrRange, endDate: targetDateOrRange, isAll: false }
            : (window.__currentExpeditionFilter || currentExpeditionFilter));

      let url = '';
      if (activeFilter.isAll) {
        url = '/api/bootstrap?all=true';
      } else if (activeFilter.startDate && activeFilter.endDate) {
        const s = activeFilter.startDate;
        const e = activeFilter.endDate;
        if (s === e) {
          const fallbackParam = forceAutoFallback ? '&auto_fallback=true' : '';
          url = `/api/bootstrap?date=${s}&window_days=0${fallbackParam}`;
        } else {
          url = `/api/bootstrap?start_date=${s}&end_date=${e}`;
        }
      }

      if (!url) {
        const expDateStr = formatLocalDateISO(new Date());
        url = `/api/bootstrap?date=${expDateStr}&window_days=0`;
      }

      const res = await fetch(url);
      const data = await res.json();
      setIsPg(data.isPg);
      setAddresses(data.addresses || []);
      setHubs(data.hubs || []);
      setTrucks(data.trucks || []);
      setDrivers(data.drivers || []);
      setDriverShifts(data.driver_shifts || []);
      setObjects(data.objects || []);
      setStopAttachments(data.stop_attachments || []);
      setReturnTickets(data.return_tickets || []);
      setClients(data.clients || []);
      setClientDefaultTimeWindows(data.client_default_time_windows || []);
      setClientContacts(data.client_contacts || []);
      setRounds(data.rounds || []);
      setJobs(data.jobs || []);
      setStops(data.stops || []);
      setUnassignedStops(data.unassigned_stops || []);
      setStopDependencies(data.stop_dependencies || []);
      setJobPauses(data.job_pauses || []);
      setDistanceMatrix(data.distance_matrix || []);
      setDistanceMatrixCount(data.distance_matrix_count || 0);
      setRealtimeEvents(data.realtime_events || []);
      setAvailableDates(data.available_dates || []);

      // If backend auto-fallback kicked in on initial boot (e.g. today has 0 stops, fell back to latest active date)
      if (forceAutoFallback && data.start_date && data.end_date) {
        const fallbackFilter = { startDate: data.start_date, endDate: data.end_date, isAll: false };
        setCurrentExpeditionFilter(fallbackFilter);
        window.__currentExpeditionFilter = fallbackFilter;
      } else if (targetDateOrRange) {
        setCurrentExpeditionFilter(activeFilter);
        window.__currentExpeditionFilter = activeFilter;
      }

      setIsDataLoaded(true);
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  useEffect(() => {
    if (isDataLoaded && videoEnded) {
      dismissSplash();
    }
  }, [isDataLoaded, videoEnded]);

  const handleAddPause = async (jobId, beforeStopId, mins = 10) => {
    try {
      const res = await fetch(`/api/jobs/${jobId}/pauses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ before_stop_id: beforeStopId, estimated_length_mins: mins })
      });
      if (res.ok) {
        await loadData();
      }
    } catch (err) {
      console.error('Error adding pause:', err);
    }
  };

  const handleDeletePause = async (pauseId) => {
    try {
      const res = await fetch(`/api/pauses/${pauseId}`, { method: 'DELETE' });
      if (res.ok) {
        setJobPauses(prev => prev.filter(p => p.id !== pauseId));
        await loadData();
      }
    } catch (err) {
      console.error('Error deleting pause:', err);
    }
  };

  useEffect(() => {
    loadData(null, 0, true);

    let ws = null;
    let reconnectTimeout = null;
    let isUnmounted = false;

    const getWsUrl = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      if (window.location.port === '3000' || window.location.port === '5173') {
        return `${protocol}//${window.location.host}/ws`;
      }
      return `${protocol}//${window.location.host}`;
    };

    const connectWs = () => {
      if (isUnmounted) return;
      try {
        ws = new WebSocket(getWsUrl());

        ws.onopen = () => {
          if (!isUnmounted) setWsConnected(true);
        };

        ws.onmessage = async (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (payload.type === 'REALTIME_DRIVER_UPDATE') {
              const { event: evt, stop, job } = payload;
              if (stop && stop.id) {
                setStops(prev => {
                  const exists = prev.some(s => String(s.id) === String(stop.id));
                  if (exists) {
                    return prev.map(s => String(s.id) === String(stop.id) ? { ...s, ...stop } : s);
                  }
                  return [...prev, stop];
                });
              }
              if (job && job.id) {
                setJobs(prev => {
                  const exists = prev.some(j => String(j.id) === String(job.id));
                  if (exists) {
                    return prev.map(j => String(j.id) === String(job.id) ? { ...j, ...job } : j);
                  }
                  return [...prev, job];
                });
              }
              if (evt) {
                setRealtimeEvents(prev => [evt, ...prev]);
              }
              await loadData();
            } else if (payload.type === 'MATRIX_RECALCULATE_PROGRESS') {
              setMatrixProgress(payload);
            } else if (payload.type === 'MATRIX_RECALCULATE_COMPLETE') {
              setMatrixProgress(null);
              if (payload.count) setDistanceMatrixCount(payload.count);
            }
          } catch (err) {
            console.error('Error parsing WS message:', err);
          }
        };

        ws.onclose = () => {
          if (!isUnmounted) {
            setWsConnected(false);
            reconnectTimeout = setTimeout(connectWs, 3000);
          }
        };

        ws.onerror = () => {
          if (!isUnmounted) {
            setWsConnected(false);
            try { ws.close(); } catch(e){}
          }
        };
      } catch (e) {
        if (!isUnmounted) {
          reconnectTimeout = setTimeout(connectWs, 3000);
        }
      }
    };

    connectWs();

    return () => {
      isUnmounted = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, []);

  // CRUD API Handlers
  const handleRecalculateMatrix = async () => {
    const res = await fetch('/api/distance-matrix/recalculate', { method: 'POST' });
    if (res.ok) await loadData();
  };

  const handleCreateJob = async (data = {}) => {
    const res = await fetch('/api/jobs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ display_name: '', ...data })
    });
    if (res.ok) {
      const newJob = await res.json();
      await loadData();
      return newJob;
    }
  };

  const handleCalculateJob = async (jobId) => {
    const res = await fetch(`/api/jobs/${jobId}/calculate`, { method: 'POST' });
    if (res.ok) await loadData();
  };

  const handleFetchOptimizationPreview = async (jobId, options = {}) => {
    try {
      const res = await fetch(`/api/jobs/${jobId}/optimize-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ options })
      });
      if (res.ok) {
        const data = await res.json();
        return data.preview || data;
      } else {
        const errText = await res.text();
        console.error('Error fetching optimization preview (HTTP ' + res.status + '):', errText);
      }
    } catch (err) {
      console.error('Error fetching optimization preview:', err);
    }
    return null;
  };

  const handleApplyOptimization = async (jobId, orderedStopIds, unassignedStopIds, recommendedStartTime = null, pauses = []) => {
    const res = await fetch(`/api/jobs/${jobId}/apply-optimization`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderedStopIds, unassignedStopIds, recommendedStartTime, pauses })
    });
    if (res.ok) await loadData();
  };

  const handleOptimizeJob = async (jobId) => {
    const res = await fetch(`/api/jobs/${jobId}/optimize`, { method: 'POST' });
    if (res.ok) await loadData();
  };

  const handleCrossOptimize = async (jobIds = [], unassignedStopIds = []) => {
    const res = await fetch('/api/jobs/cross-optimize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobIds, unassignedStopIds })
    });
    if (res.ok) await loadData();
  };

  const handleUpdateJob = async (jobId, jobData) => {
    const res = await fetch(`/api/jobs/${jobId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(jobData)
    });
    if (res.ok) await loadData();
  };

  const handleDeleteJob = async (jobId) => {
    const res = await fetch(`/api/jobs/${jobId}`, { method: 'DELETE' });
    if (res.ok) await loadData();
  };

  const handleAssignStop = async (jobId, unassignedId) => {
    const res = await fetch(`/api/jobs/${jobId}/assign-stop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ unassignedId })
    });
    if (res.ok) await loadData();
  };

  const handleReorderStops = async (jobId, stopIds, isRealIndex = false) => {
    const res = await fetch(`/api/jobs/${jobId}/reorder-stops`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stopIds, isRealIndex })
    });
    if (res.ok) await loadData();
  };

  const handleAddStop = async (stopData) => {
    const res = await fetch('/api/stops', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(stopData)
    });
    if (res.ok) await loadData();
  };

  const handleUpdateStop = async (stopId, stopData) => {
    const res = await fetch(`/api/stops/${stopId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(stopData)
    });
    if (res.ok) await loadData();
  };

  const handleDeleteStop = async (stopId) => {
    const res = await fetch(`/api/stops/${stopId}`, { method: 'DELETE' });
    if (res.ok) await loadData();
  };

  const handleDriverCompleteStop = async (stopId, status, notes) => {
    const res = await fetch(`/api/stops/${stopId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, driver_notes: notes })
    });
    if (res.ok) await loadData();
  };

  const handleDriverStartJob = async (jobId) => {
    const res = await fetch(`/api/jobs/${jobId}/start`, { method: 'POST' });
    if (res.ok) await loadData();
  };

  const handleDriverFinishJob = async (jobId) => {
    const res = await fetch(`/api/jobs/${jobId}/finish`, { method: 'POST' });
    if (res.ok) await loadData();
  };

  const handleAddTruck = async (truckData) => {
    const res = await fetch('/api/trucks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(truckData)
    });
    if (res.ok) await loadData();
  };

  const handleUpdateTruck = async (id, truckData) => {
    const res = await fetch(`/api/trucks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(truckData)
    });
    if (res.ok) await loadData();
  };

  const handleDeleteTruck = async (id) => {
    const res = await fetch(`/api/trucks/${id}`, { method: 'DELETE' });
    if (res.ok) await loadData();
  };

  const handleAddHub = async (hubData) => {
    const res = await fetch('/api/hubs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(hubData)
    });
    if (res.ok) await loadData();
  };

  const handleUpdateHub = async (id, hubData) => {
    const res = await fetch(`/api/hubs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(hubData)
    });
    if (res.ok) await loadData();
  };

  const handleAddClient = async (clientData) => {
    const res = await fetch('/api/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(clientData)
    });
    if (res.ok) await loadData();
  };

  const handleUpdateClient = async (id, clientData) => {
    const res = await fetch(`/api/clients/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(clientData)
    });
    if (res.ok) loadData();
  };

  const handleAddClientTimeWindow = async (clientId, timeWindowData) => {
    const res = await fetch(`/api/clients/${clientId}/time-windows`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(timeWindowData)
    });
    if (res.ok) await loadData();
    return res.ok;
  };

  const handleUpdateClientTimeWindow = async (id, timeWindowData) => {
    const res = await fetch(`/api/clients/time-windows/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(timeWindowData)
    });
    if (res.ok) await loadData();
    return res.ok;
  };

  const handleDeleteClientTimeWindow = async (id) => {
    const res = await fetch(`/api/clients/time-windows/${id}`, { method: 'DELETE' });
    if (res.ok) await loadData();
    return res.ok;
  };

  const handleAddContact = async (clientId, contactData) => {
    const res = await fetch(`/api/clients/${clientId}/contacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contactData)
    });
    if (res.ok) loadData();
  };

  const handleDeleteContact = async (contactId) => {
    const res = await fetch(`/api/contacts/${contactId}`, { method: 'DELETE' });
    if (res.ok) loadData();
  };

  const handleAddDriver = async (driverData) => {
    const res = await fetch('/api/drivers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(driverData)
    });
    if (res.ok) loadData();
  };

  const handleUpdateDriver = async (id, driverData) => {
    const res = await fetch(`/api/drivers/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(driverData)
    });
    if (res.ok) loadData();
  };

  const handleAddRound = async (roundData) => {
    const res = await fetch('/api/rounds', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(roundData)
    });
    if (res.ok) loadData();
  };

  const handleUpdateRound = async (id, roundData) => {
    const res = await fetch(`/api/rounds/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(roundData)
    });
    if (res.ok) loadData();
  };

  const handleDeleteRound = async (id) => {
    const res = await fetch(`/api/rounds/${id}`, { method: 'DELETE' });
    if (res.ok) loadData();
  };

  const handleDeleteAllPlannedJobs = async () => {
    const res = await fetch('/api/jobs/planned', { method: 'DELETE' });
    if (res.ok) await loadData();
  };

  // 1. Mobile Driver Mode
  if (isNative || isDriverRoute || isExplicitConnect) {
    if (showDriverConnect || !savedDriverId) {
      return (
        <DriverConnectPage
          initialServerUrl={savedServerUrl}
          initialDriverId={savedDriverId}
          onConnect={(driverId, serverUrl) => {
            setSavedDriverId(driverId);
            setSavedServerUrl(serverUrl);
            setShowDriverConnect(false);
          }}
        />
      );
    }

    return (
      <DriverAppPage
        driverId={savedDriverId}
        serverUrl={savedServerUrl}
        onSwitchServerOrDriver={() => {
          setSavedDriverId('');
          setShowDriverConnect(true);
          if (typeof window !== 'undefined' && window.location.pathname.startsWith('/driver/')) {
            window.history.pushState(null, '', '/connect');
          }
        }}
      />
    );
  }

  // 2. Authentication Check
  if (!currentUser || !authToken) {
    return (
      <LoginPage
        onLoginSuccess={(user, token) => {
          setCurrentUser(user);
          setAuthToken(token);
          loadData();
        }}
      />
    );
  }

  return (
    <div className="app-container" style={{ position: 'relative' }}>
      {isInitialLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            background: '#090d16',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: splashFading ? 0 : 1,
            transition: 'opacity 0.6s ease-in-out',
            pointerEvents: splashFading ? 'none' : 'auto'
          }}
          onClick={dismissSplash}
        >
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <video
              src="/brand/animated_logo.mp4"
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnded}
              style={{
                width: '440px',
                maxWidth: '90vw',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(56, 189, 248, 0.25)'
              }}
            />
            <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#38bdf8', fontSize: '0.92rem', fontWeight: '700' }}>
                {!isDataLoaded && (
                  <span style={{ border: '2px solid rgba(56, 189, 248, 0.2)', borderTop: '2px solid #38bdf8', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                )}
                <span>
                  {isDataLoaded ? `Chargement de ${stops.length} arrêts` : 'Chargement des arrêts...'}
                </span>
              </div>

              {isDataLoaded && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismissSplash();
                  }}
                  style={{
                    background: 'rgba(56, 189, 248, 0.15)',
                    color: '#38bdf8',
                    border: '1px solid #38bdf8',
                    padding: '8px 20px',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '800',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 0 15px rgba(56, 189, 248, 0.25)',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span>Passer</span>
                  <span style={{ fontSize: '0.75rem' }}>▶</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <Navbar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenSimulator={() => setIsSimulatorOpen(true)}
        isPg={isPg}
        wsConnected={wsConnected}
        currentUser={currentUser}
        onLogout={handleLogout}
        impersonatedTenantName={impersonatedTenantName}
        onClearImpersonate={handleClearImpersonate}
      />

      <main className="page-content">
        {activeTab === 'expedition' && (
          <ExpeditionPage
            hubs={hubs}
            jobs={jobs}
            stops={stops}
            clients={clients}
            clientDefaultTimeWindows={clientDefaultTimeWindows}
            drivers={drivers}
            driverShifts={driverShifts}
            trucks={trucks}
            rounds={rounds}
            unassignedStops={unassignedStops}
            stopDependencies={stopDependencies}
            jobPauses={jobPauses}
            realtimeEvents={realtimeEvents}
            onCalculateJob={handleCalculateJob}
            onFetchOptimizationPreview={handleFetchOptimizationPreview}
            onApplyOptimization={handleApplyOptimization}
            onOptimizeJob={handleOptimizeJob}
            onCrossOptimize={handleCrossOptimize}
            onAssignStop={handleAssignStop}
            onUpdateStop={handleUpdateStop}
            onDeleteStop={handleDeleteStop}
            onUpdateJob={handleUpdateJob}
            onDeleteJob={handleDeleteJob}
            onReorderStops={handleReorderStops}
            onCreateJob={handleCreateJob}
            onAddPause={handleAddPause}
            onDeletePause={handleDeletePause}
            onReloadData={loadData}
            onDeleteAllPlannedJobs={handleDeleteAllPlannedJobs}
            dateFilter={currentExpeditionFilter}
            onDateFilterChange={(newFilter) => {
              setCurrentExpeditionFilter(newFilter);
              window.__currentExpeditionFilter = newFilter;
              loadData(newFilter);
            }}
            availableDates={availableDates}
          />
        )}

        {activeTab === 'connect' && (
          <ConnectPage />
        )}

        {activeTab === 'admin' && currentUser?.role === 'admin' && (
          <AdminTenantsPage
            token={authToken}
            currentImpersonateId={impersonatedTenantId}
            onSwitchTenant={handleSwitchTenant}
          />
        )}

        {activeTab === 'database' && (
          <DatabasePage
            addresses={addresses}
            hubs={hubs}
            clients={clients}
            clientDefaultTimeWindows={clientDefaultTimeWindows}
            clientContacts={clientContacts}
            rounds={rounds}
            trucks={trucks}
            drivers={drivers}
            driverShifts={driverShifts}
            objects={objects}
            stops={stops}
            unassignedStops={unassignedStops}
            stopDependencies={stopDependencies}
            jobs={jobs}
            distanceMatrix={distanceMatrix}
            distanceMatrixCount={distanceMatrixCount}
            matrixProgress={matrixProgress}
            onRecalculateMatrix={handleRecalculateMatrix}
            onAddHub={handleAddHub}
            onUpdateHub={handleUpdateHub}
            onAddClient={handleAddClient}
            onUpdateClient={handleUpdateClient}
            onAddClientTimeWindow={handleAddClientTimeWindow}
            onUpdateClientTimeWindow={handleUpdateClientTimeWindow}
            onDeleteClientTimeWindow={handleDeleteClientTimeWindow}
            onAddTruck={handleAddTruck}
            onUpdateTruck={handleUpdateTruck}
            onDeleteTruck={handleDeleteTruck}
            onAddDriver={handleAddDriver}
            onUpdateDriver={handleUpdateDriver}
            onAddStop={handleAddStop}
            onUpdateStop={handleUpdateStop}
            onDeleteStop={handleDeleteStop}
            onAddRound={handleAddRound}
            onUpdateRound={handleUpdateRound}
            onDeleteRound={handleDeleteRound}
            onUpdateJob={handleUpdateJob}
            onAddContact={handleAddContact}
            onDeleteContact={handleDeleteContact}
            onReloadData={loadData}
            currentTenantId={impersonatedTenantId || currentUser?.tenantId || 1}
            currentTenantName={impersonatedTenantName || currentUser?.tenantName || 'Green Future Logistics'}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsPage
            jobs={jobs}
            stops={stops}
            trucks={trucks}
            drivers={drivers}
            rounds={rounds}
            clients={clients}
            hubs={hubs}
          />
        )}

        {activeTab === 'profile' && (
          <ProfilePage
            isPg={isPg}
            onReloadData={loadData}
            currentTenantId={impersonatedTenantId || currentUser?.tenantId || 1}
            currentTenantName={impersonatedTenantName || currentUser?.tenantName || 'Green Future Logistics'}
            currentUser={currentUser}
          />
        )}
        {activeTab === 'docs' && <DocumentationPage />}
      </main>
    </div>
  );
}
